import type { Undefinable } from '@ps/utils';
import { Option, Result } from '@sapphire/result';
import {
  CHILD_AGE_QUALIFICATION_NAME,
  CHILD_AGE_UNIT_CODE,
  CHILD_GENDER_CONDITION_CODE,
  CHILDREN_LEGACY_CONDITION_CODE,
  CHILDREN_QUALIFICATION_NAME,
  ConditionLabels,
  conditionLabelsFrom,
  HAVE_CHILDREN_QUALIFICATION_NAME,
  NO_CHILDREN_QUALIFICATION_NAME,
  QUALIFICATION_CODE,
  Quota,
  QuotaCriteria,
  selectedConditions,
  SurveyQualification,
  decodeChildAgeGenderConditionCodes,
  toChildAgeGenderQualificationConditionCodes,
  toChildAgeGenderQuotaConditionCodes,
  toChildrenLegacyQualificationConditionCodes,
} from '@ps/survey-utils';

export type ChildAgeUnitToken = 'month' | 'year';

const CHILD_AGE_UNIT_CODE_BY_TOKEN: Record<ChildAgeUnitToken, number> = {
  month: CHILD_AGE_UNIT_CODE.MONTHS,
  year: CHILD_AGE_UNIT_CODE.YEARS,
};

export interface QualificationConditionCodes {
  readonly qualificationCode: number;
  readonly conditionCodes: string[];
}

// Outbound: the 218 flag's own condition_codes are already real numeric codes (e.g. ['112']),
// so no symbolic-token translation is needed at this level — only the qualification/condition
// codes swap to their 232 equivalents.
export function toChildAgeGenderQualification(
  qualification: QualificationConditionCodes,
): QualificationConditionCodes {
  if (qualification.qualificationCode !== QUALIFICATION_CODE.CHILDREN_LEGACY) {
    return qualification;
  }
  return {
    qualificationCode: QUALIFICATION_CODE.CHILD_AGE_GENDER,
    conditionCodes: toChildAgeGenderQualificationConditionCodes(
      qualification.conditionCodes,
    ),
  };
}

// Inbound reverse of toChildAgeGenderQualification.
export function toChildrenLegacyQualification(
  qualification: QualificationConditionCodes,
): QualificationConditionCodes {
  if (qualification.qualificationCode !== QUALIFICATION_CODE.CHILD_AGE_GENDER) {
    return qualification;
  }
  return {
    qualificationCode: QUALIFICATION_CODE.CHILDREN_LEGACY,
    conditionCodes: toChildrenLegacyQualificationConditionCodes(
      qualification.conditionCodes,
    ),
  };
}

export function isChildAgeGenderQuota(quota: Quota): boolean {
  return (
    quota.groupKey === HAVE_CHILDREN_QUALIFICATION_NAME ||
    quota.groupKey === NO_CHILDREN_QUALIFICATION_NAME
  );
}

// purespectrum-lib's condition-code table carries no display text of its own (age/isBaby/isBoy
// only) — the real, buyer-facing condition names live on the 232 qualification's own `data`
// entry in survey-creation's audience state, the same source StandardSurveyPayloadMapper already
// reads for the top-level qualifications[] payload. Built once per outbound payload and threaded
// down to toChildAgeGenderCriterion rather than looked up per call.
export function childAgeGenderConditionNameLookup(
  qualifications: SurveyQualification[],
): ConditionLabels {
  const qualification = qualifications.find(
    (candidate) =>
      candidate.qualificationCode === QUALIFICATION_CODE.CHILD_AGE_GENDER,
  );
  if (!qualification || qualification.isRangeType) {
    return new Map();
  }
  return conditionLabelsFrom(qualification.data);
}

function _ageUnitCode(unit: Undefinable<string>): Option<number> {
  return unit === 'month' || unit === 'year'
    ? Option.some(CHILD_AGE_UNIT_CODE_BY_TOKEN[unit])
    : Option.none;
}

function _conditionsForGender(
  isBoy: boolean,
  isGirl: boolean,
): { conditionCodes: string[]; conditionNames: string[] } {
  if (isBoy && isGirl) {
    return {
      conditionCodes: [
        String(CHILD_GENDER_CONDITION_CODE.BOY),
        String(CHILD_GENDER_CONDITION_CODE.GIRL),
      ],
      conditionNames: ['Boy', 'Girl'],
    };
  }
  return isBoy
    ? {
        conditionCodes: [String(CHILD_GENDER_CONDITION_CODE.BOY)],
        conditionNames: ['Boy'],
      }
    : {
        conditionCodes: [String(CHILD_GENDER_CONDITION_CODE.GIRL)],
        conditionNames: ['Girl'],
      };
}

// child_age_unit (311/312) has no home on the shared QuotaCriteria model — legacy carries it as
// a sibling wire field next to condition_codes on the 232 criterion, and this mapper deliberately
// doesn't touch shared models/DTOs (see README in this folder). Handed back/taken in explicitly
// so whoever wires this into the real payload mapper decides where it lives on their own DTO.
export interface ChildAgeGenderOutboundCriterion {
  readonly criterion: QuotaCriteria;
  readonly childAgeUnit: number;
}

// Why a quota could not be converted. The two cases are genuinely different — a quota with no
// criteria at all is malformed, whereas an in-progress row is the expected state of a drawer the
// buyer is still filling in — so they stay distinct rather than collapsing into one absence.
export type ChildAgeGenderCriterionFailure = 'noCriteria' | 'incompleteRow';

export type ChildAgeGenderCriterionResult = Result<
  ChildAgeGenderOutboundCriterion,
  ChildAgeGenderCriterionFailure
>;

// Outbound, single quota: the drawer's one merged have-children criterion (age range + real
// gender condition codes together, see CHILD_AGE_GENDER_CRITERION_OVERRIDES) or one-criterion
// no-children quota into a single real 232 QuotaCriteria. Errs rather than throwing on a row that
// cannot be converted, so the call site can drop in-progress rows from the outbound payload and
// still know why. `conditionNameByCode` is the 232 qualification's own condition-code → name
// table (see childAgeGenderConditionNameLookup) — the only source of the real, buyer-facing
// names; purespectrum-lib carries no display text of its own. A code missing from the table is
// omitted rather than shipped nameless, mirroring selectedConditions' own contract.
export function toChildAgeGenderCriterion(
  quota: Quota,
  conditionNameByCode: ConditionLabels,
): ChildAgeGenderCriterionResult {
  const criterion = quota.criteria[0];
  if (!criterion) {
    return Result.err('noCriteria');
  }

  if (quota.groupKey === NO_CHILDREN_QUALIFICATION_NAME) {
    const noChildren = selectedConditions(
      toChildAgeGenderQuotaConditionCodes(
        String(CHILDREN_LEGACY_CONDITION_CODE.NONE),
      ),
      conditionNameByCode,
    );
    return Result.ok({
      childAgeUnit: CHILD_AGE_UNIT_CODE.YEARS,
      criterion: {
        ...criterion,
        qualificationCode: QUALIFICATION_CODE.CHILD_AGE_GENDER,
        qualificationName: CHILDREN_QUALIFICATION_NAME,
        conditionCodes: noChildren.map(({ id }) => id),
        conditionNames: noChildren.map(({ name }) => name),
        rangeMin: undefined,
        rangeMax: undefined,
        rangeUnit: undefined,
        rangeSets: [],
      },
    });
  }

  const {
    rangeMin,
    rangeMax,
    conditionCodes: genderConditionCodes,
  } = criterion;

  // Every precondition of a complete have-children row, in one pipeline: a named age unit, at
  // least one gender, and both bounds. Joining them this way hands the success branch bounds
  // already narrowed to numbers, so the wire call below needs no re-checking.
  return _ageUnitCode(criterion.rangeUnit)
    .filter(() => !!genderConditionCodes.length)
    .andThen((ageUnit) =>
      Option.all([
        Option.from(rangeMin).filter(Number.isInteger),
        Option.from(rangeMax).filter(Number.isInteger),
      ]).map(([from, to]) => ({ ageUnit, from, to })),
    )
    .match<ChildAgeGenderCriterionResult, ChildAgeGenderCriterionResult>({
      some: ({ ageUnit, from, to }) => {
        const haveChildren = selectedConditions(
          toChildAgeGenderQuotaConditionCodes(
            String(CHILDREN_LEGACY_CONDITION_CODE.HAVE),
            genderConditionCodes,
            { units: ageUnit, from, to },
          ),
          conditionNameByCode,
        );
        return Result.ok({
          childAgeUnit: ageUnit,
          criterion: {
            ...criterion,
            qualificationCode: QUALIFICATION_CODE.CHILD_AGE_GENDER,
            qualificationName: CHILDREN_QUALIFICATION_NAME,
            conditionCodes: haveChildren.map(({ id }) => id),
            conditionNames: haveChildren.map(({ name }) => name),
            rangeMin: undefined,
            rangeMax: undefined,
            rangeUnit: undefined,
            rangeSets: [],
          },
        });
      },
      none: () => Result.err('incompleteRow'),
    });
}

// Inbound reverse of toChildAgeGenderCriterion — a passthrough for every other qualification, so
// it's safe to map over a full criteria array unconditionally. childAgeUnit is the persisted
// 311/312 the caller reads off their own DTO (see ChildAgeGenderOutboundCriterion) — defaults to
// Years so a caller that hasn't wired child_age_unit through yet still gets a usable result.
export function fromChildAgeGenderCriterion(
  criterion: QuotaCriteria,
  childAgeUnit: number = CHILD_AGE_UNIT_CODE.YEARS,
): QuotaCriteria {
  if (criterion.qualificationCode !== QUALIFICATION_CODE.CHILD_AGE_GENDER) {
    return criterion;
  }

  const decoded = decodeChildAgeGenderConditionCodes(
    criterion.conditionCodes,
    childAgeUnit,
  );

  if (decoded.noChildren) {
    return {
      ...criterion,
      qualificationCode: QUALIFICATION_CODE.CHILDREN_LEGACY,
      qualificationName: NO_CHILDREN_QUALIFICATION_NAME,
      conditionCodes: [String(CHILDREN_LEGACY_CONDITION_CODE.NONE)],
      conditionNames: ['No'],
    };
  }

  const ageUnitToken: ChildAgeUnitToken =
    childAgeUnit === CHILD_AGE_UNIT_CODE.MONTHS ? 'month' : 'year';
  const { conditionCodes, conditionNames } = _conditionsForGender(
    decoded.isBoy,
    decoded.isGirl,
  );

  return {
    ...criterion,
    qualificationCode: QUALIFICATION_CODE.CHILDREN_LEGACY,
    qualificationName: CHILD_AGE_QUALIFICATION_NAME,
    conditionCodes,
    conditionNames,
    rangeMin: decoded.ageFrom,
    rangeMax: decoded.ageTo,
    rangeUnit: ageUnitToken,
    rangeSets: [
      { units: childAgeUnit, from: decoded.ageFrom, to: decoded.ageTo },
    ],
  };
}
